import type {
  PolicyConsentStatusFieldsFragment,
  PolicyDocument,
} from 'apollo/generated/client-operations';

/**
 * The minimal policy-document fields needed to derive a label + PDF URL and
 * write consent back. Matches the pre-auth `getCurrentPolicyDocuments` selection
 * (account-creation step), which returns `{ id key version }`.
 */
export type PolicyDocumentFragment = Pick<
  PolicyDocument,
  'id' | 'key' | 'version'
>;

/**
 * A current policy paired with the user's consent history for its `key`. Drives
 * the re-capture gate rows. Shared via the `PolicyConsentStatusFields` fragment
 * so both session queries that feed the gate — `getCurrentSession` (AppLayout)
 * and `getSelfOnboardingContainerSession` (self-onboarding flow) — produce this
 * one type.
 */
export type PolicyConsentStatusFragment = PolicyConsentStatusFieldsFragment;

/** True when the user has consented to this policy's current version. */
export function isPolicyAccepted(status: PolicyConsentStatusFragment): boolean {
  return status.lastAcceptedVersion === status.document.version;
}

/**
 * Format a policy timestamp (ISO string) for display, e.g. "June 1, 2026".
 * Rendered in UTC so the stored calendar date is shown as-is, not shifted by the
 * viewer's timezone (and so it's deterministic).
 */
export function formatPolicyDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const POLICY_DOC_KEY_LINK_MAP: Record<string, string> = {
  'privacy-policy': 'https://www.useodin.com/privacy-policy',
  'terms-of-use': 'https://www.useodin.com/terms-of-use',
  'software-license':
    'https://cdn.prod.website-files.com/60ef27fe4f5e1a246bc555ab/6a186e0ea37dcea651509704_Final%20Odin%20Labs%2C%20Inc.%20-%20Website%20Software%20License%20v5.26%20-%205.26.2026.pdf',
};

export function getPolicyDocumentUrl(policyKey: string): string {
  return POLICY_DOC_KEY_LINK_MAP[policyKey] ?? 'https://www.useodin.com/legal';
}
